import { useState, useEffect } from 'react';
import { fetchAPI, UrlParams } from '../services/strapi';

function mapResponse(result: any, urlPrefix = "") {
  return {
    title: result.attributes.title,
    author: result.attributes.author,
    date: result.attributes.visual_date,
    pdf_url: result.attributes.file_url
  };
}

export type Search = {
  buscaLivre?: string;
  author?: string;
  initialDate?: string;
  finalDate?: string;
  indice?: string;
  types?: string[];
}

type apiResponse = {
  data: any[],
  meta: {
		pagination: {
			page: number,
			pageSize: number,
			pageCount: number,
			total: number
		}
	}
}

export type Response = {
  results: any[],
  pagination: {
    page: number,
    pageSize: number,
    pageCount: number,
    total: number
	}
}

function mountFilter(array: any[], field: string | undefined | null, fieldTitle: string, operator: string = "$containsi") {
  if(field) {
    let filter: any = {};
    filter[fieldTitle] = {};
    filter[fieldTitle][operator] = field;

    array.push(filter);
  }
}

function mountCaseSensitiveFilter(array: any[], field: string | undefined | null, fieldTitle: string, operator: string = "$containsi") {
  if(field) {
    const tempArray: any[] = [];
    mountFilter(tempArray, field, fieldTitle, operator);
    mountFilter(tempArray, `${field}`.toUpperCase(), fieldTitle, operator);
    mountFilter(tempArray, `${field}`.toLowerCase(), fieldTitle, operator);

    array.push({
      $or: tempArray
    })
  }
}

function mapFilter(search: Search) {
  let filters: any = {};

  let or: any[] = [];
  if(search?.buscaLivre) {
    mountCaseSensitiveFilter(or, search?.buscaLivre, 'search_text');
    mountCaseSensitiveFilter(or, search?.buscaLivre, 'title');
    mountCaseSensitiveFilter(or, search?.buscaLivre, 'author');
    mountCaseSensitiveFilter(or, search?.buscaLivre, 'file_url');
    mountCaseSensitiveFilter(or, search?.buscaLivre, 'type');
    mountCaseSensitiveFilter(or, search?.buscaLivre, 'indice');
    
    filters.$or = or;
  }

  let and: any[] = []
  mountFilter(and, search?.initialDate, 'date', '$gte');
  mountFilter(and, search?.finalDate, 'date', '$lte');

  mountCaseSensitiveFilter(and, search?.author, 'author');
  mountFilter(and, search?.indice, 'indice');

  if(search?.types && search?.types?.length ) {
    and.push({
      type: {
        //$in: search.types
        $in: search?.types.reduce((previous: string[], current: string) => previous.concat(mapTypeValues(current)) , [])
      }
    });
  }

  filters.$and = and;

  return filters
}

function mapTypeValues(initSearch: string): string[] {
  switch (initSearch) {

    case "Livro":
      return ["Livro", "Livros"]
    case "Capítulo de Livro":
      return ["Capítulo de Livro", "Capítulos e Artigos"]
    case "Artigo em Periódico":
      return ["Artigo em Periódico"]
    case "Relatórios de pesquisa":
      return ["Relatórios de pesquisa"]
    case "Documentos Institucionais":
      return ["Documentos Institucionais"]
    case "Anais de Congressos":
      return ["Anais de Congressos"]
    case "Paletras e Apresentações":
      return ["Paletras e Apresentações"]
    case "Guias e manuais":
      return ["Guias e manuais"]
    case "Outros":
      return ["Outro", "Outros"]
    case "Formatos da produção":
      return ["Formatos da produção"]
    case "Texto":
      return ["Texto"]
    case "Audiovisual":
      return ["Audiovisual"]
    case "Gráficos, Mapas Mentais, Fluxos e Ilustrações":
      return ["Gráficos, Mapas Mentais, Fluxos e Ilustrações"]
    case "Áudio":
      return ["Áudio"]
    default:
      return []
  }
}

export function mapInitialSearch(initSearch: string, array: any[]|null = null): string[] {
  switch (initSearch) {
    case "livros":
      array && mountFilter(array, "Livro", 'type')
      array && mountFilter(array, "Livros", 'type')
      return ["Livro", "Livros"]
    case "capitulos-artigos":
      array && mountFilter(array, "Capítulo de Livro", 'type')
      array && mountFilter(array, "Capítulos e Artigos", 'type')
      return ["Capítulo de Livro", "Capítulos e Artigos"]
    case "pesquisas-empiricas":
      array && mountFilter(array, "Relatórios de pesquisa", 'type')
      return ["Relatórios de pesquisa"]
    case "estudos-institucionais":
      array && mountFilter(array, "Documentos Institucionais", 'type')
      array && mountFilter(array, "Estudo Institucional", 'type')
      return ["Estudo Institucional", "Documentos Institucionais"]
    case "anais-congressos":
      array && mountFilter(array, "Anais de Congressos", 'type')
      return ["Anais de Congressos"]
    case "guias-manuais":
      array && mountFilter(array, "Guias e manuais", 'type')
      return ["Guias e manuais"]
    case "audiovisual":
      array && mountFilter(array, "Audiovisual", 'type')
      return ["Audiovisual"]
    case "outros":
      array && mountFilter(array, "Outro", 'type')
      array && mountFilter(array, "Outros", 'type')
      return ["Outro", "Outros"]
    default:
      return []
  }
}

export function usePublications(initialSearch: string = ''): Response {
  const initialData: Response = {
    results: [], 
    pagination: {
      page: -1,
      pageCount: -1,
      pageSize: -1,
      total: -1
    }
  }
  const [data, setData] = useState<Response>(initialData);
  
  async function fetchData() {
    let query: UrlParams = {
      populate: "*",
      pagination: {
        page: 1,
        pageSize: 10
      },
      sort: ['date:desc', 'title:asc'],
    };

    if (mapInitialSearch(initialSearch)) {
      let or: any[] = [];
      mapInitialSearch(initialSearch, or);
      query.filters = {
        $or: or
      }
    }

    setData(await makeRequest(query));
  }

  useEffect(() => {
    //if(data?.results?.length) return;
    fetchData();
  }, [data.pagination.page, initialSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  return data;
};

export const orderObjects = [
  {
    frontValue: 'Mais Recentes',
    backValue: 'date:desc'
  },
  {
    frontValue: 'Mais Antigas',
    backValue: 'date:asc'
  },
  {
    frontValue: 'Alfabética A - Z',
    backValue: 'title:asc'
  },
  {
    frontValue: 'Alfabética Z - A',
    backValue: 'title:desc'
  }
]

export async function getPublications(search: Search | null = null,  page: number = 1, order: string = orderObjects[0].backValue): Promise<Response> {
  let query: UrlParams = {
    populate: "*",
    pagination: {
      page: page,
      pageSize: 10
    },
    sort: [order],
  };
  if(search) {
    query.filters = mapFilter(search);
  }
  return await makeRequest(query);
}


async function makeRequest(query: any): Promise<Response> {
  const response: apiResponse = await fetchAPI("/publications", query);
    return {
      results: response.data.map((item: any) => mapResponse(item)),
      pagination: response.meta.pagination
    };
}